import type { Asset, AssetType, OccupancyExpectation } from "@/contracts";

import i18n from "@/i18n";
import { EM_DASH, formatWeekdays } from "@/lib/format";

/**
 * Asset vocabulary in one place.
 *
 * The list reaches well past refrigeration on purpose: the product manages
 * IoT devices and whatever they watch, and cold chain is one vertical rather
 * than the whole of it (ADR-045).
 */
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  get REFRIGERATOR() {
    return i18n.t("pages:assetType.REFRIGERATOR");
  },
  get FREEZER() {
    return i18n.t("pages:assetType.FREEZER");
  },
  get COLD_ROOM() {
    return i18n.t("pages:assetType.COLD_ROOM");
  },
  get DISPLAY_CASE() {
    return i18n.t("pages:assetType.DISPLAY_CASE");
  },
  get TRANSPORT_UNIT() {
    return i18n.t("pages:assetType.TRANSPORT_UNIT");
  },
  get STORAGE_ROOM() {
    return i18n.t("pages:assetType.STORAGE_ROOM");
  },
  get SERVER_ROOM() {
    return i18n.t("pages:assetType.SERVER_ROOM");
  },
  get PRODUCTION_AREA() {
    return i18n.t("pages:assetType.PRODUCTION_AREA");
  },
  get MEETING_ROOM() {
    return i18n.t("pages:assetType.MEETING_ROOM");
  },
  get TANK() {
    return i18n.t("pages:assetType.TANK");
  },
  get CIRCUIT() {
    return i18n.t("pages:assetType.CIRCUIT");
  },
  get OTHER() {
    return i18n.t("pages:assetType.OTHER");
  },
};

/**
 * Expected occupancy, phrased as what it does rather than as its enum name.
 *
 * "Not monitored" says the platform is not judging presence here, which is
 * different from asserting the space is empty — the distinction the whole
 * three-state model exists for (ADR-069).
 */
export const OCCUPANCY_LABELS: Record<OccupancyExpectation, string> = {
  get UNMONITORED() {
    return i18n.t("pages:occupancy.UNMONITORED");
  },
  get NEVER() {
    return i18n.t("pages:occupancy.NEVER");
  },
  get SCHEDULED() {
    return i18n.t("pages:occupancy.SCHEDULED");
  },
};

/**
 * The hours in one cell, for a table.
 *
 * A scheduled asset reads "Mon to Fri, 08:00–19:00". The other two states
 * have no hours to show, so they show what they are instead — an empty cell
 * would read as "nobody filled this in", which is true of exactly one of
 * them.
 */
export function formatOccupancy(
  asset: Pick<Asset, "occupancy" | "occupancyDays" | "occupancyStart" | "occupancyEnd">,
): string {
  if (asset.occupancy !== "SCHEDULED") {
    return OCCUPANCY_LABELS[asset.occupancy];
  }

  if (!asset.occupancyStart || !asset.occupancyEnd) {
    return EM_DASH;
  }

  return `${formatWeekdays(asset.occupancyDays)}, ${asset.occupancyStart}\u2013${asset.occupancyEnd}`;
}
